import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useDeal, useFriends, useVault, useDealPayments } from '../api/hooks';
import { STAGE_LABEL, STAGE_ORDER, DealStage } from '../lib/types';
import { Stack, Row, Text, Avatar, Badge, Panel, List, ListItem, Money, Tag, Button, Card, Divider, EmptyState, Input, BlacklistAlertBadge, DealMenu, ArtifactView } from '../ui';
import clsx from 'clsx';
import { api } from '../api/client';
import { DealEconomics } from './DealEconomics';
import { DealParties } from './DealParties';
import { PaymentsTable } from './PaymentsTable';
import { NewPaymentModal } from './NewPaymentModal';
import { DocManagement } from './DocManagement';

type Tab = 'overview' | 'economics' | 'parties' | 'documents' | 'counterparties' | 'agents' | 'payments';

export function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('overview');
  const { data, loading, refresh } = useDeal(id);
  const { data: friends } = useFriends();
  const { data: allArtifacts, refresh: refreshVault } = useVault();
  const { data: dealPayments, refresh: refreshPayments } = useDealPayments(id);
  const [econ, setEcon] = useState<any>(null);
  useEffect(() => {
    if (!id || !data?.deal) return;
    api.dealEconomics(id).then(setEcon).catch(() => setEcon(null));
  }, [id, data?.deal?.updatedAt]);
  const [stageHistory, setStageHistory] = useState<any[]>([]);
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const r: any = await api.stageHistory(id);
        if (!cancelled) setStageHistory(Array.isArray(r?.history) ? r.history : []);
      } catch (e) {
        console.warn('[stageHistory] failed:', e);
        if (!cancelled) setStageHistory([]);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);
  const [newPaymentOpen, setNewPaymentOpen] = useState(false);
  const [newCost, setNewCost] = useState({ description: '', amount: '', date: new Date().toISOString().slice(0,10) });
  const [newBlocker, setNewBlocker] = useState({ description: '', ownerId: '' });
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [newPart, setNewPart] = useState({ friendId: '', role: 'contributor', splitBp: 0 });
  const [partBusy, setPartBusy] = useState(false);
  const [partErr, setPartErr] = useState<string | null>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<any>(null);
  const [kycName, setKycName] = useState('');
  const [kycJurisdiction, setKycJurisdiction] = useState('');
  const [kycBusy, setKycBusy] = useState(false);
  const [ddBusy, setDdBusy] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadType, setUploadType] = useState<'kyc'|'dd'|'term-sheet'|'upload'>('upload');
  const fileRef = useRef<HTMLInputElement>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  if (loading) return <Text muted variant="small">loading…</Text>;
  if (!data) return <Stack gap={2}><Link to="/deals" className="text-xs text-muted hover:text-ink">← deals</Link><Text variant="display">deal not found</Text></Stack>;

  const { deal: d, participants, blockers, ledger, visibility } = data;
  const dealArtifacts = (allArtifacts ?? []).filter((a: any) => a.dealId === id);
  const friendById = (id: string) => friends.find((f: any) => f.id === id);
  const lead = friendById(d.leadFriendId);

  async function addCost() {
    if (!id || !newCost.description || !newCost.amount) return;
    await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/deals/${id}/ledger`, {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ type: 'cost', description: newCost.description, amount: parseFloat(newCost.amount), currency: d.currency, date: newCost.date }),
    });
    setNewCost({ description: '', amount: '', date: new Date().toISOString().slice(0,10) });
    refresh();
  }
  async function addBlocker() {
    if (!id || !newBlocker.description || !newBlocker.ownerId) return;
    await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/deals/${id}/blockers`, {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(newBlocker),
    });
    setNewBlocker({ description: '', ownerId: '' });
    refresh();
  }
  async function resolveBlocker(bid: string) {
    if (!id) return;
    await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/deals/${id}/blockers/${bid}/resolve`, { method: 'POST', credentials: 'include' });
    refresh();
  }
  async function changeStage(stage: DealStage) {
    if (!id) return;
    await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/deals/${id}/stage`, {
      method: 'POST', credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ stage }),
    });
    refresh();
  }

  async function uploadFile(file: File) {
    if (!id) return;
    setUploadBusy(true);
    try {
      await api.uploadDoc(file, { dealId: id, type: uploadType });
      refreshVault();
      refresh();
    } catch (e: any) {
      alert('upload failed: ' + e.message);
    } finally {
      setUploadBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function runKyc() {
    if (!id || !kycName) return;
    setKycBusy(true);
    try {
      await api.runKyc(kycName, id, kycJurisdiction || undefined);
      setKycName(''); setKycJurisdiction('');
      refresh(); refreshVault();
    } catch (e: any) {
      alert('kyc failed: ' + e.message);
    } finally {
      setKycBusy(false);
    }
  }

  async function draftDd() {
    if (!id) return;
    setDdBusy(true);
    try {
      await api.draftDd(id);
      refreshVault();
    } catch (e: any) {
      alert('dd draft failed: ' + e.message);
    } finally {
      setDdBusy(false);
    }
  }

  const totalCosts = (ledger ?? []).filter((l: any) => l.type === 'cost').reduce((a: number, l: any) => a + l.amount, 0);
  const expectedRevenue = d.expectedRevenue ?? 0;

  // Parse flag summary for banner
  const flagSummary = d.listFlagSummary
    ? (() => { try { return JSON.parse(d.listFlagSummary); } catch { return []; } })()
    : [];
  const flagHitName = flagSummary[0]?.partyName ?? '';
  const flagHitReason = flagSummary[0]?.hits?.[0]?.reason ?? '';

  return (
    <Stack gap={3}>
      <Link to="/deals" className="text-xs text-muted hover:text-ink">← deals</Link>

      {/* Compliance banner */}
      {d.listFlagStatus === 'blacklist_hit' && !bannerDismissed && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-sm bg-negative/10 border border-negative/40">
          <span className="text-base mt-px">&#x26A0;</span>
          <div className="flex-1 text-sm">
            <span className="font-semibold text-negative">Compliance alert:</span>{' '}
            <span className="text-negative">
              {flagHitName ? `${flagHitName} appears on the group blacklist` : 'A party appears on the group blacklist'}
              {flagHitReason ? ` (${flagHitReason})` : ''}.
            </span>{' '}
            <span className="text-negative/80">Review before proceeding.</span>
          </div>
          <button onClick={() => setBannerDismissed(true)} className="text-negative/60 hover:text-negative text-xs ml-2 mt-0.5">dismiss</button>
        </div>
      )}

      <div>
        <Row gap={2} align="center" wrap justify="between">
          <Row gap={2} align="center" wrap>
            <Text variant="display">{d.name}</Text>
            {d.heat && <Badge tone={d.heat === 'hot' ? 'negative' : d.heat === 'warm' ? 'warning' : 'neutral'}>{d.heat}</Badge>}
          </Row>
          {id && <DealMenu dealId={id} state={d.deletedAt ? 'deleted' : d.archivedAt ? 'archived' : 'active'} onChange={() => navigate('/deals')} />}
        </Row>
        <Row gap={2} className="mt-1">
          <Text muted variant="small">{d.type} · {d.sector}{d.geography ? ` · ${d.geography}` : ''}</Text>
          {d.ticketSize && <Money amount={d.ticketSize} currency={d.currency || 'USD'} className="text-sm" />}
        </Row>
      </div>

      <Card padded={false}>
        <div className="flex overflow-x-auto no-scrollbar">
          {STAGE_ORDER.map((s, i) => {
            const active = s === d.stage;
            const passed = STAGE_ORDER.indexOf(d.stage) > i;
            return (
              <button key={s} onClick={() => changeStage(s)}
                disabled={visibility !== 'all' && visibility !== 'most'}
                className={clsx(
                  'shrink-0 flex-1 min-w-[80px] px-2 py-2 hairline-r last:border-r-0 text-center text-[11px] sm:text-xs tracking-tight transition-colors',
                  active && 'bg-ink text-bg',
                  passed && !active && 'text-ink',
                  !passed && !active && 'text-muted',
                  (visibility === 'all' || visibility === 'most') && !active && 'hover:bg-whisper cursor-pointer',
                )}>{STAGE_LABEL[s]}</button>
            );
          })}
        </div>
      </Card>

      {/* Tabs - scrollable on mobile, snap */}
      <div className="flex overflow-x-auto whitespace-nowrap hairline-b no-scrollbar -mx-0">
        {(['overview','economics','parties','documents','counterparties','agents','payments'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={clsx(
            'snap-start shrink-0 px-3 h-10 text-xs sm:text-sm transition-colors duration-[120ms]',
            tab === t ? 'text-ink border-b-2 border-ink -mb-px font-medium' : 'text-muted hover:text-ink',
          )}>{t}</button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <section className="md:col-span-8">
            <Stack gap={3}>
              <Panel title="Blockers">
                {(blockers ?? []).length === 0
                  ? <Text variant="small" muted>No blockers — deal is moving.</Text>
                  : <Stack gap={2}>
                      {blockers.map((b: any) => {
                        const owner = friendById(b.ownerId);
                        const sinceDays = Math.floor((Date.now() - b.createdAt) / 86400000);
                        return (
                          <Row key={b.id} gap={2} align="start">
                            <span className="mt-1 h-1.5 w-1.5 rounded-full bg-negative shrink-0" />
                            <Stack gap={0.5} className="flex-1">
                              <Text variant="small">{b.description}</Text>
                              <Text variant="label">{owner?.name ?? b.ownerId} · {sinceDays}d</Text>
                            </Stack>
                            <Button variant="ghost" size="sm" onClick={() => resolveBlocker(b.id)}>resolve</Button>
                          </Row>
                        );
                      })}
                    </Stack>}
                <Divider className="my-3" />
                <Stack gap={1}>
                  <Text variant="micro">add blocker</Text>
                  <Row gap={1}>
                    <Input value={newBlocker.description} onChange={e => setNewBlocker({ ...newBlocker, description: e.target.value })} placeholder="what's blocking this deal?" className="flex-1" />
                    <select value={newBlocker.ownerId} onChange={e => setNewBlocker({ ...newBlocker, ownerId: e.target.value })} className="h-9 hairline rounded-sm px-2 bg-bg text-sm">
                      <option value="">owner…</option>
                      {participants.map((p: any) => { const f = friendById(p.friendId); return f && <option key={f.id} value={f.id}>{f.name}</option>; })}
                    </select>
                    <Button variant="primary" size="sm" onClick={addBlocker}>add</Button>
                  </Row>
                </Stack>
              </Panel>
            </Stack>
          </section>

          <aside className="md:col-span-4">
            <Stack gap={3}>
              <Panel title="Lead">
                {lead && <Row gap={2} align="center"><Avatar name={lead.name} friendId={lead.id} hasAvatar={!!lead.avatarUrl} size="md" /><Stack gap={0.5}><Text variant="small" className="font-medium">{lead.name}</Text><Text variant="label">{lead.city ?? '—'}</Text></Stack></Row>}
              </Panel>
              <Panel
                title={`${participants.length} friends`}
                action={visibility === 'all' ? <Button variant="ghost" size="sm" onClick={() => { setShowAddParticipant(s => !s); setPartErr(null); }}>+ add</Button> : undefined}
              >
                <Stack gap={2}>
                  {(() => {
                    const totalBp = participants.reduce((s: number, p: any) => s + (p.splitBp ?? 0), 0);
                    const warnSplits = visibility === 'all' && totalBp !== 10000;
                    return (
                      <>
                        {warnSplits && <Text variant="label" className="text-amber-500">splits = {(totalBp/100).toFixed(0)}% — adjust to balance</Text>}
                        {participants.map((p: any) => {
                          const f = friendById(p.friendId);
                          if (!f) return null;
                          if (visibility !== 'all') {
                            return <Row key={p.friendId} gap={2} justify="between" align="center"><Row gap={2} align="center"><Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" /><Stack gap={0.5}><Text variant="small">{f.name}</Text><Text variant="label">{p.role}</Text></Stack></Row><span className="tnum text-sm text-muted">{(p.splitBp/100).toFixed(0)}%</span></Row>;
                          }
                          return (
                            <Row key={p.friendId} gap={1} align="center" className="group">
                              <Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" />
                              <Stack gap={0} className="flex-1 min-w-0">
                                <Text variant="small">{f.name}</Text>
                              </Stack>
                              <select
                                value={p.role}
                                className="h-9 sm:h-7 text-xs hairline rounded-sm px-2 sm:px-1 bg-bg"
                                onChange={async e => { await api.patchParticipant(d.id, f.id, { role: e.target.value }); refresh(); }}
                              >
                                {['lead','contributor','silent','observer'].map(r => <option key={r} value={r}>{r}</option>)}
                              </select>
                              <input
                                type="number" min="0" max="100" step="1"
                                value={(p.splitBp/100).toFixed(0)}
                                className="w-14 sm:w-12 h-9 sm:h-7 text-xs hairline rounded-sm px-2 sm:px-1 bg-bg tnum text-right"
                                onBlur={async e => {
                                  const bp = Math.round(parseFloat(e.target.value || '0') * 100);
                                  if (!isNaN(bp)) { await api.patchParticipant(d.id, f.id, { splitBp: bp }); refresh(); }
                                }}
                              />
                              <Text variant="label">%</Text>
                              {p.role !== 'lead' && (
                                <button
                                  className="opacity-0 group-hover:opacity-100 ml-1 text-negative text-xs hover:opacity-100 transition-opacity"
                                  title="remove"
                                  onClick={async () => {
                                    try { await api.removeParticipant(d.id, f.id); refresh(); setPartErr(null); }
                                    catch (e: any) { setPartErr(e.message); }
                                  }}
                                >x</button>
                              )}
                            </Row>
                          );
                        })}
                        {partErr && <Text variant="label" className="text-negative">{partErr}</Text>}
                        {showAddParticipant && visibility === 'all' && (() => {
                          const taken = new Set(participants.map((p: any) => p.friendId));
                          const available = friends.filter((f: any) => !taken.has(f.id));
                          return (
                            <Stack gap={1} className="hairline rounded-sm p-2 bg-whisper">
                              <Text variant="micro">add participant</Text>
                              <Row gap={1} wrap>
                                <select
                                  value={newPart.friendId}
                                  onChange={e => setNewPart(p => ({ ...p, friendId: e.target.value }))}
                                  className="flex-1 h-8 text-sm hairline rounded-sm px-2 bg-bg"
                                >
                                  <option value="">pick a friend…</option>
                                  {available.map((f: any) => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                                <select
                                  value={newPart.role}
                                  onChange={e => setNewPart(p => ({ ...p, role: e.target.value }))}
                                  className="h-8 text-sm hairline rounded-sm px-2 bg-bg"
                                >
                                  {['contributor','silent','observer','lead'].map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                                <div className="flex items-center gap-1">
                                  <input
                                    type="number" min="0" max="100" step="1" placeholder="split %"
                                    value={newPart.splitBp > 0 ? (newPart.splitBp/100).toFixed(0) : ''}
                                    className="w-16 h-8 text-sm hairline rounded-sm px-2 bg-bg tnum"
                                    onChange={e => setNewPart(p => ({ ...p, splitBp: Math.round(parseFloat(e.target.value||'0')*100) }))}
                                  />
                                  <Text variant="label">%</Text>
                                </div>
                              </Row>
                              <Row gap={1}>
                                <Button variant="primary" size="sm" disabled={partBusy || !newPart.friendId}
                                  onClick={async () => {
                                    setPartBusy(true); setPartErr(null);
                                    try {
                                      await api.addParticipant(d.id, { friendId: newPart.friendId, role: newPart.role, splitBp: newPart.splitBp });
                                      setNewPart({ friendId: '', role: 'contributor', splitBp: 0 });
                                      setShowAddParticipant(false);
                                      refresh();
                                    } catch (e: any) { setPartErr(e.message); }
                                    finally { setPartBusy(false); }
                                  }}>{partBusy ? 'adding…' : 'add'}</Button>
                                <Button variant="ghost" size="sm" onClick={() => { setShowAddParticipant(false); setPartErr(null); }}>cancel</Button>
                              </Row>
                            </Stack>
                          );
                        })()}
                      </>
                    );
                  })()}
                </Stack>
              </Panel>
              <Panel title="At a glance">
                <Stack gap={2}>
                  <Row justify="between">
                    <Text variant="label">expected revenue</Text>
                    {econ?.annual?.revenue > 0
                      ? <Money amount={econ.annual.revenue} currency={d.currency} className="text-sm" />
                      : econ?.contractTotal?.gross > 0
                        ? <Money amount={econ.contractTotal.gross} currency={d.currency} className="text-sm" />
                        : <Text variant="small" muted>—</Text>}
                  </Row>
                  <Row justify="between">
                    <Text variant="label">expected margin</Text>
                    {econ?.perShipment?.marginPct > 0
                      ? <span className="tnum text-sm">{(econ.perShipment.marginPct * 100).toFixed(0)}%</span>
                      : econ?.deal?.spreadPerUnit > 0 && econ?.deal?.sell?.effective > 0
                        ? <span className="tnum text-sm">{((econ.deal.spreadPerUnit / econ.deal.sell.effective) * 100).toFixed(0)}%</span>
                        : <Text variant="small" muted>—</Text>}
                  </Row>
                  <Row justify="between">
                    <Text variant="label">profit / unit</Text>
                    {econ?.deal?.spreadPerUnit > 0
                      ? <Text variant="small" className="tnum">{d.currency} {econ.deal.spreadPerUnit.toFixed(2)} / {d.volumeUnit ?? 'unit'}</Text>
                      : <Text variant="small" muted>—</Text>}
                  </Row>
                  <Row justify="between">
                    <Text variant="label">contract total gross</Text>
                    {econ?.contractTotal?.gross > 0
                      ? <Money amount={econ.contractTotal.gross} currency={d.currency} className="text-sm" />
                      : <Text variant="small" muted>—</Text>}
                  </Row>
                  <Row justify="between">
                    <Text variant="label">total costs (logged)</Text>
                    <Money amount={totalCosts} currency={d.currency} className="text-sm" />
                  </Row>
                </Stack>
              </Panel>
              {Array.isArray(stageHistory) && stageHistory.length > 0 && (
                <Panel title="Stage timeline">
                  <Stack gap={1}>
                    {stageHistory.slice(0, 8).map((h: any) => (
                      <div key={h.id} className="hairline-b py-1.5">
                        <Row justify="between">
                          <Stack gap={0.5}>
                            <Row gap={1} wrap>
                              <Text variant="small" className="font-medium">{h.from_stage ?? '(start)'} → {h.to_stage}</Text>
                            </Row>
                            {h.note && <Text variant="label" className="break-words">{h.note}</Text>}
                            <Text variant="label">{h.by_name ?? 'someone'} · {new Date(h.created_at).toLocaleString()}</Text>
                          </Stack>
                        </Row>
                      </div>
                    ))}
                  </Stack>
                </Panel>
              )}
            </Stack>
          </aside>
        </div>
      )}

      {tab === 'economics' && <DealEconomics dealId={id!} deal={d} ledger={ledger} participants={participants} friendById={friendById} addCost={addCost} newCost={newCost} setNewCost={setNewCost} expectedRevenue={expectedRevenue} totalCosts={totalCosts} />}

      {tab === 'parties' && <DealParties dealId={id!} />}

      {tab === 'documents' && (
        <Stack gap={3}>
          <Panel title="Run KYC on a document" action={
            <select value={uploadType} onChange={e => setUploadType(e.target.value as any)} className="h-8 text-xs hairline rounded-sm px-2 bg-bg">
              <option value="kyc">KYC package</option>
              <option value="upload">general upload</option>
              <option value="dd">due diligence</option>
              <option value="term-sheet">term sheet</option>
            </select>
          }>
            <Stack gap={2}>
              <Text variant="small" muted>upload a doc or KYC package — the deal agent will extract entities, check sanctions, and flag risks. once reviewed, you can create CRM profiles from extracted entities.</Text>
              <Row gap={1}>
                <input ref={fileRef} type="file" onChange={e => e.target.files?.[0] && uploadFile(e.target.files[0])} className="text-sm flex-1" disabled={uploadBusy} />
                {uploadBusy && <Text variant="label">uploading…</Text>}
              </Row>
            </Stack>
          </Panel>

          <Panel title="Run KYC by counterparty name">
            <Stack gap={2}>
              <Text variant="small" muted>sanctions, PEP, adverse media, ownership chain. report lands in the vault and on this deal.</Text>
              <Row gap={1}>
                <Input value={kycName} onChange={e => setKycName(e.target.value)} placeholder="counterparty name" className="flex-1" />
                <Input value={kycJurisdiction} onChange={e => setKycJurisdiction(e.target.value)} placeholder="jurisdiction (optional)" className="w-48" />
                <Button variant="primary" onClick={runKyc} disabled={kycBusy || !kycName}>{kycBusy ? 'screening…' : 'run kyc'}</Button>
              </Row>
            </Stack>
          </Panel>

          <Panel title="Documents on this deal" action={<Button variant="secondary" size="sm" onClick={draftDd} disabled={ddBusy}>{ddBusy ? 'drafting…' : 'draft dd memo'}</Button>}>
            <DocManagement
              artifacts={dealArtifacts.filter((a: any) => !a.deletedAt)}
              friends={friends}
              parties={(participants ?? []).map((p: any) => ({
                id: p.partyId ?? p.id,
                legalName: p.legalName ?? '',
                contactName: p.contactName ?? '',
                role: p.role ?? '',
              }))}
              myName={lead?.name ?? 'Me'}
              onRefresh={() => { refresh(); refreshVault(); }}
            />
          </Panel>


        </Stack>
      )}

      {tab === 'counterparties' && <Panel title="Counterparties"><EmptyState title="run a kyc check from the documents tab to add a counterparty" /></Panel>}

      {tab === 'agents' && <Panel title="Deal agent log"><EmptyState title="agent activity log" hint="every kyc run, dd draft, document upload, stage change is recorded here" /></Panel>}

      {tab === 'payments' && (
        <Panel title={`Payments (${(dealPayments ?? []).length})`} action={<Button variant="secondary" size="sm" onClick={() => setNewPaymentOpen(true)}>+ add expected payment</Button>}>
          <PaymentsTable payments={dealPayments ?? []} onRefresh={refreshPayments} />
        </Panel>
      )}

      <NewPaymentModal open={newPaymentOpen} onClose={() => setNewPaymentOpen(false)} onSuccess={refreshPayments} dealId={id} dealName={data?.deal?.name} />
    </Stack>
  );
}